import { ComponentType } from 'react';

import { BanIcon, IconProps, KeyIcon, OrgIcon, PinIcon, PrinterIcon, SlidersIcon } from './icons';

export interface AdminNavItem {
  id: string;
  to: string;
  labelKey: string;
  descriptionKey: string;
  Icon: ComponentType<IconProps>;
}

export interface AdminNavGroup {
  id: string;
  headingKey: string;
  items: AdminNavItem[];
}

const GUEST_CHECKPOINTS: AdminNavItem = {
  id: 'guestCheckpoints',
  to: '/admin/guest-checkpoints',
  labelKey: 'guestCheckpoint.titlePlural',
  descriptionKey: 'header.descriptions.guestCheckpoints',
  Icon: PinIcon,
};

const GUEST_PERMIT_PRINTING: AdminNavItem = {
  id: 'guestPermitPrinting',
  to: '/admin/guestpermit-printing',
  labelKey: 'guestPermits.printingTitle',
  descriptionKey: 'header.descriptions.guestPermitPrinting',
  Icon: PrinterIcon,
};

const ELECTRONIC_KEY: AdminNavItem = {
  id: 'electronicKey',
  to: '/admin/electronic-key',
  labelKey: 'electronicKey.printingTitle',
  descriptionKey: 'header.descriptions.electronicKey',
  Icon: KeyIcon,
};

const RESTRICTED_LIST: AdminNavItem = {
  id: 'restrictedList',
  to: '/admin/restricted-list',
  labelKey: 'blackList.titlePlural',
  descriptionKey: 'header.descriptions.restrictedList',
  Icon: BanIcon,
};

const ORG_VIEW: AdminNavItem = {
  id: 'orgView',
  to: '/org-view',
  labelKey: 'orgView.title',
  descriptionKey: 'header.descriptions.orgView',
  Icon: OrgIcon,
};

const SETTINGS: AdminNavItem = {
  id: 'settings',
  to: '/admin/settings',
  labelKey: 'settings.title',
  descriptionKey: 'header.descriptions.settings',
  Icon: SlidersIcon,
};

/**
 * Külalisload and Statistika are plain top-level nav links (`main-nav.tsx` and
 * `mobile-drawer.tsx` both render one), not Administreerimine panel items — kept here anyway so
 * both places share the same route/label pair instead of duplicating literals.
 */
export const GUEST_PERMITS_NAV = { to: '/gc/guest-permits', labelKey: 'guestPermits.titlePlural' } as const;
export const STATISTICS_NAV = { to: '/admin/statistics', labelKey: 'statistics.title' } as const;

export interface AdminGroupsInput {
  /** The active role, not a held one — mirrors the old `AdminMenu`, which only ever rendered for `isActiveAdmin`. */
  isAdmin: boolean;
  isOrgViewWhitelisted: boolean;
}

/**
 * Builds the Administreerimine panel's groups, already filtered down to what's visible: a group
 * is omitted entirely when none of its items apply. An org-view-only, non-admin user therefore
 * gets a single "Süsteem" group containing just Organisatsiooni vaade — the Administreerimine
 * trigger still shows for them, per spec.
 */
export function getAdminGroups({ isAdmin, isOrgViewWhitelisted }: AdminGroupsInput): AdminNavGroup[] {
  const groups: AdminNavGroup[] = [];

  if (isAdmin) {
    groups.push({
      id: 'issuing',
      headingKey: 'header.groups.issuing',
      items: [GUEST_CHECKPOINTS, GUEST_PERMIT_PRINTING, ELECTRONIC_KEY],
    });
    groups.push({
      id: 'control',
      headingKey: 'header.groups.control',
      items: [RESTRICTED_LIST],
    });
  }

  const systemItems: AdminNavItem[] = [];
  if (isOrgViewWhitelisted) systemItems.push(ORG_VIEW);
  if (isAdmin) systemItems.push(SETTINGS);
  if (systemItems.length > 0) {
    groups.push({ id: 'system', headingKey: 'header.groups.system', items: systemItems });
  }

  return groups;
}

/** Flattens the grouped panel data into a single list, for the mobile drawer's ungrouped listing. */
export function flattenAdminItems(groups: AdminNavGroup[]): AdminNavItem[] {
  return groups.flatMap(group => group.items);
}
